// client/src/lib/payFlow.ts
/**
 * The Pay button's fetch-link → open-page step, as pure logic (Sentry LAMIRAGECO-DP, 2026-11-04).
 *
 * A client pressed Pay, the payment page opened, they pressed Pay again with another gateway and the
 * in-app browser refused ("Unable to display URL" — one was already presented). Nothing told them:
 * the payment just did not happen. Now every outcome that is not "the page opened" comes back with a
 * message the caller shows, and the button is disabled while a pay is in flight.
 */

/** The ONE public venue number (memory: feedback_public_phone_number). */
export const PAY_OPEN_FAILED_MESSAGE =
  "Couldn't open the payment page. Please try again, or contact us on +91 94467 76666.";

/** The server refused with a message meant for the client ("This invoice is already paid."). */
export class PayLinkError extends Error {
  constructor(message: string) { super(message); this.name = "PayLinkError"; }
}

export type PayOpenOutcome = { ok: true } | { ok: false; message: string };

/**
 * getUrl: fetch the payment link (throw PayLinkError to show the server's own words; anything else —
 * a network failure, a parse error — gets the standard message). open: resolves true when opened.
 */
export async function openPaymentLink(
  getUrl: () => Promise<string | null | undefined>,
  open: (url: string) => Promise<boolean>,
): Promise<PayOpenOutcome> {
  let url: string | null | undefined;
  try {
    url = await getUrl();
  } catch (e) {
    return { ok: false, message: e instanceof PayLinkError && e.message ? e.message : PAY_OPEN_FAILED_MESSAGE };
  }
  if (!url) return { ok: false, message: PAY_OPEN_FAILED_MESSAGE };
  let opened = false;
  try { opened = await open(url); } catch { opened = false; }
  return opened ? { ok: true } : { ok: false, message: PAY_OPEN_FAILED_MESSAGE };
}

/**
 * A synchronous in-flight guard: React state disables the button on the next render, but two taps can
 * land before it. run(task) resolves true when it ran, false when it was ignored (one already running).
 */
export function createSingleFlight(): (task: () => Promise<unknown>) => Promise<boolean> {
  let busy = false;
  return async (task) => {
    if (busy) return false;
    busy = true;
    try { await task(); return true; } finally { busy = false; }
  };
}
